import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RecoveryCodeResponse, TwoFactorSetupResponse, TwoFactorStatus } from '../_models/2fa';
import { User, UserCreate, UserIndex, UserUpdate } from '../_models/user';
import { map } from 'rxjs/operators';
import { httpOptions } from '../_helpers/utils';
import { UserUpdateResponse } from '../_models/api-responses';
import { PaginationData } from '../_models/pagination-data.model';
import { ErrorObject } from '../_models/error';

/** Raw laboratory shape returned by the users index endpoint. */
interface ApiUserLab {
  labId: number;
  labName: string;
}

/** Raw user shape from the users index endpoint (labs use labId/labName). */
type ApiIndexUser = Omit<User, 'laboratories'> & {
  laboratories?: ApiUserLab[];
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);

  httpOptions = httpOptions;

  private readonly createUserCache$ = new BehaviorSubject<UserCreate>(null);

  public get retrieveAndClearUnsavedUserCache(): UserCreate {
    const cached = this.createUserCache$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.createUserCache$.next(null);
  }

  onUpdateCreateUserCache = (user: UserCreate): void => {
    this.createUserCache$.next(user);
  };

  inviteUser(user: UserCreate): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/invite`,
        user,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          if (response.body == null) {
            throw {
              status: response.status,
              message: 'Something went wrong',
              code: null,
            };
          }
          return response.body;
        }),
      );
  }

  deleteUserInvite(userId: number): Observable<string> {
    return this.http
      .delete<string>(
        `${environment.apiUrl}/users/invite/${userId}`,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  resendUserInvite(userId: number): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/invite/${userId}/resend`,
        null,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  resendConfirmation(userId: number): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/${userId}/resend-confirmation`,
        null,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  resendUpdateEmailConfirmation(userId: number): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/${userId}/resend-email-change`,
        null,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  getUserDetails(userId: number): Observable<User> {
    return this.http
      .get<User>(`${environment.apiUrl}/users/${userId}`, this.httpOptions)
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  updateUser(user: UserUpdate): Observable<UserUpdateResponse> {
    return this.http
      .patch<User>(
        `${environment.apiUrl}/users/${user?.id}`,
        user,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          const res: UserUpdateResponse = {};
          if (response.status === 200) {
            res.user = response.body;
            res.message = 'User details have been updated.';
            return res;
          }
          if (response.status === 201) {
            res.message =
              'Update user request is pending approval; the status can be checked on the approvals page.';
            return res;
          }
          return res;
        }),
      );
  }

  updatePassword(
    userId: number,
    currentPassword: string,
    newPassword: string,
  ): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/users/${userId}/password`,
        { currentPassword, NewPassword: newPassword },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  updateEmail(userId: number, email: string): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/users/${userId}/email`,
        { email: email },

        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  confirmEmail(userId: number, token: string): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/${userId}/confirm-email`,
        { token },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  updateEmailConfirmation(userId: number, token: string): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/${userId}/update-email-confirmation`,
        { token },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  deleteUser(userId: number): Observable<unknown> {
    return this.http
      .delete<unknown>(
        `${environment.apiUrl}/users/${userId}`,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          // Return a message based on the status code
          // Plus a boolean to indicate if the user should be logged out
          switch (response.status) {
            case 200:
              return {
                message: 'The user has been deleted.',
                logout: true,
              };
            case 201:
              return {
                message:
                  'Delete user request is pending approval; the status can be checked on the approvals page.',
                logout: false,
              };
            default:
              throw Error('Something went wrong');
          }
        }),
      );
  }

  disableAccount(userId: number, isDisabled: boolean): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/users/${userId}/disabled`,
        { isDisabled },
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }

  unlockAccount(userId: number): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/users/${userId}/unlock`,
        null,
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }

  signOutOtherDevices(): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/logout/other-devices`,
        null,
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }

  getAllUsers(
    sortBy = 'FirstName',
    sortOrder = 'asc',
    pageNumber = 1,
    pageSize = 5,
    search?: string,
    role?: string,
  ): Observable<UserIndex> {
    const searchParam = search ? `&Search=${encodeURIComponent(search)}` : '';
    const roleParam = role ? `&Role=${encodeURIComponent(role)}` : '';
    const url = `${environment.apiUrl}/users/all?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageNumber=${pageNumber}&PageSize=${pageSize}${searchParam}${roleParam}`;
    return this.http.get<ApiIndexUser[]>(url, this.httpOptions).pipe(
      map((response) => {
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination') ?? '{}',
        ) as PaginationData;
        const body =
          response.status === 200 && response.body ? response.body : [];
        const users: User[] = body.map((user) => ({
          ...user,
          laboratories: user.laboratories?.map((lab) => ({
            laboratoryId: lab.labId,
            laboratoryName: lab.labName,
          })),
        }));
        return { paginationData, users };
      }),
    );
  }

  getTwoFactorStatus(): Observable<TwoFactorStatus> {
    return this.http
      .get<TwoFactorStatus>(`${environment.apiUrl}/users/me/2fa`, this.httpOptions)
      .pipe(map((response) => response.body));
  }

  setupTwoFactor(): Observable<TwoFactorSetupResponse> {
    return this.http
      .post<TwoFactorSetupResponse>(
        `${environment.apiUrl}/users/me/2fa/setup`,
        null,
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }

  enableTwoFactor(code: string): Observable<RecoveryCodeResponse> {
    return this.http
      .post<RecoveryCodeResponse>(
        `${environment.apiUrl}/users/me/2fa/enable`,
        { code },
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }

  disableTwoFactor(password: string): Observable<void> {
    return this.http
      .post<void>(
        `${environment.apiUrl}/users/me/2fa/disable`,
        { password },
        this.httpOptions,
      )
      .pipe(map(() => void 0));
  }

  generateRecoveryCodes(password: string, code: string): Observable<RecoveryCodeResponse> {
    return this.http
      .post<RecoveryCodeResponse>(
        `${environment.apiUrl}/users/me/2fa/recovery-codes`,
        { password, code },
        this.httpOptions,
      )
      .pipe(map((response) => this.requireRecoveryCodes(response.body)));
  }

  private requireRecoveryCodes(response: RecoveryCodeResponse): RecoveryCodeResponse {
    if (!response?.recoveryCodes?.length) {
      const error: ErrorObject = {
        status: 0,
        message:
          'No recovery codes were returned. Please try again or contact support.',
        code: null,
      };
      throw error;
    }
    return response;
  }

  adminResetTwoFactor(userId: number): Observable<void> {
    return this.http
      .post<void>(
        `${environment.apiUrl}/users/${userId}/2fa/reset`,
        null,
        this.httpOptions,
      )
      .pipe(map(() => void 0));
  }

  getOrganisationUsers(
    id: number,
    sortBy = 'FirstName',
    sortOrder = 'asc',
    pageNumber = 1,
    pageSize = 5,
  ): Observable<UserIndex> {
    const url = `${environment.apiUrl}/users/${id}/organisation-members?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageNumber=${pageNumber}&PageSize=${pageSize}`;
    return this.http.get<User[]>(url, this.httpOptions).pipe(
      map((response) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination') ?? '{}',
        ) as PaginationData;
        const users = response.body;
        return { paginationData, users };
      }),
    );
  }
}
